import { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../constants';

interface Bill {
  date?: string;
  due?: string;
  payDate?: string;
  amount?: number;
  id?: string;
  [key: string]: any;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('de-CH', { style: 'currency', currency: 'CHF' });

const formatDate = (value?: string) => {
  if (!value) return '--.--.----';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getBillId = (bill: Bill) => bill.id || '—';

const getBillDate = (bill: Bill) => bill.date || '';

const getBillAmount = (bill: Bill) => {
  const amount = typeof bill.amount === 'number' ? bill.amount : Number(bill.amount ?? 0);
  return Number.isFinite(amount) ? amount / 100 : 0;
};

const readStoredPatientId = () => {
  const raw = localStorage.getItem('loginPatientId');
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object') {
      const candidate = (parsed as { patientId?: unknown; id?: unknown }).patientId ?? (parsed as { id?: unknown }).id;
      return typeof candidate === 'string' ? candidate : '';
    }
    return '';
  } catch {
    return raw.trim();
  }
};

export function BillPage() {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const payBill = () => {};
  const downloadBill = (_index: number) => {};

  useEffect(() => {
    const fetchBills = async () => {
      const token = localStorage.getItem('token');
      const patientId = readStoredPatientId();

      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      if (!patientId) {
        setBills([]);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/bills/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ patientId }),
        });

        if (!response.ok) throw new Error('Suche erfolglos');
        const billIds = await response.json();
        if (!Array.isArray(billIds)) {
          setBills([]);
          return;
        }

        const billPromises = billIds.map(async (billId: string) => {
          const billResponse = await fetch(`${API_BASE_URL}/bills/id/${billId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!billResponse.ok) throw new Error(`Fehler ${billId}`);
          return billResponse.json();
        });

        const billDetails = await Promise.all(billPromises);
        setBills(billDetails);
      } catch (err) {
        setError(`Fehler: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchBills();
  }, []);

  const today = useMemo(() => new Date(), []);

  const dueBills = useMemo(() => {
    return bills.filter((bill) => {
      if (bill.payDate) return false;
      if (!bill.due) return false;
      const dueDate = new Date(bill.due);
      if (Number.isNaN(dueDate.getTime())) return false;
      return dueDate <= today;
    });
  }, [bills, today]);

  const dueAmount = useMemo(
    () => dueBills.reduce((sum, bill) => sum + getBillAmount(bill), 0),
    [dueBills]
  );

  const historyBills = useMemo(() => {
    const dueSet = new Set(dueBills.map((bill) => getBillId(bill)));
    const combined = [...dueBills, ...bills.filter((bill) => !dueSet.has(getBillId(bill)))];
    return combined.slice(0, 3);
  }, [bills, dueBills]);

  if (loading) return <div className="text-center p-4">Loading bills...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;

  return (
    <div className="portal-page-surface text-slate-900">
      <div className="portal-page-content">
        <div className="portal-page-heading mb-10">
          <h1 className="text-3xl font-semibold text-slate-900">
            Meine <span className="font-bold">Rechnungen</span>
          </h1>
          <p className="mt-2 text-sm text-slate-400">
              Übersicht uber offene und beglichene Rechnungen.
          </p>
        </div>

        <div className="rounded-[28px] border border-[#efe7da] bg-white px-6 py-6 shadow-[0_14px_30px_rgba(19,19,19,0.05)]">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff1f2]">
                <svg className="h-6 w-6 text-[#f43f5e]" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <circle cx="8" cy="9" r="1" fill="currentColor" />
                </svg>
              </div>
              <div>
                <p className="text-base font-semibold text-slate-900">Offener Betrag</p>
                {dueBills.length === 0 ? (
                  <p className="mt-1 text-sm text-[#f43f5e]">Keine fälligen Rechnungen</p>
                ) : (
                  <div className="mt-2 space-y-1 text-sm text-[#f43f5e]">
                    {dueBills.map((bill) => (
                      <p key={`due-${getBillId(bill)}`}>
                        {formatCurrency(getBillAmount(bill))} · Zahlbar bis {formatDate(bill.due)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
              <p className="text-lg font-semibold text-slate-900">{formatCurrency(dueAmount)}</p>
              <button
                type="button"
                onClick={payBill}
                className="rounded-xl bg-[#cbb58a] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_20px_rgba(203,181,138,0.3)]"
              >
                Jetzt bezahlen
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">Rechnungshistorie</h2>
          <button
            type="button"
            className="rounded-lg border border-[#efe7da] bg-white px-3 py-1 text-xs text-slate-500"
          >
            Letzte 6 Monate
          </button>
        </div>

        <div className="mt-4 overflow-hidden rounded-3xl border border-[#efe7da] bg-white shadow-[0_14px_30px_rgba(19,19,19,0.05)]">
          <div className="grid grid-cols-[1.2fr,1fr,1.4fr,0.9fr,0.9fr,0.6fr] gap-4 bg-[#fcfbf8] px-6 py-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Rechnungs ID</span>
            <span>Datum</span>
            <span>Behandlung</span>
            <span>Betrag</span>
            <span>Status</span>
            <span className="text-center">PDF</span>
          </div>

          {historyBills.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400">Keine Rechnungen gefunden.</div>
          ) : (
            historyBills.map((bill, index) => {
              const paid = Boolean(bill.payDate);
              const due = !paid && Boolean(bill.due) && new Date(bill.due as string) <= today;
              return (
                <div
                  key={`${getBillId(bill)}-${index}`}
                  className="grid grid-cols-[1.2fr,1fr,1.4fr,0.9fr,0.9fr,0.6fr] items-center gap-4 border-t border-[#f2ece1] px-6 py-5 text-sm"
                >
                  <span className="text-slate-500">{getBillId(bill)}</span>
                  <span className="text-slate-500">{formatDate(getBillDate(bill))}</span>
                  <div>
                    <p className="font-semibold text-slate-900">Behandlung</p>
                    <p className="text-xs text-slate-400">Dr. med. dent. Muller</p>
                  </div>
                  <span className="text-slate-500">{formatCurrency(getBillAmount(bill))}</span>
                  <span
                    className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                      paid
                        ? 'bg-[#f2f4f7] text-slate-400'
                        : due
                          ? 'bg-[#fff1f2] text-[#f43f5e]'
                          : 'bg-[#f5f5f5] text-slate-500'
                    }`}
                  >
                    {paid ? 'paid' : 'open'}
                  </span>
                  <button
                    type="button"
                    onClick={() => downloadBill(index)}
                    className="flex flex-col items-center text-xs text-slate-400"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 4v10m0 0l4-4m-4 4l-4-4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 18h14"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                    PDF
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-10 rounded-3xl border border-[#efe7da] bg-white px-6 py-6 text-sm text-slate-500 shadow-[0_14px_30px_rgba(19,19,19,0.05)]">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f5efe3] text-[#cbb58a]">
              i
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Haben Sie Fragen zu Ihrer Rechnung?</p>
              <p className="mt-1">
                Rufen Sie uns gerne direkt an unter <span className="text-[#cbb58a]">052 625 46 25</span> oder
                nutzen Sie unseren Chat.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
